// Release Tracking — three views over GitLab prod_deploy events:
//   1. Releases (time-clustered) — what shipped together
//   2. Unmapped MRs — landed in prod without a Jira key (actionable)
//   3. By Assignee — who's missing Jira tracking, sorted by violation count

import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { PULSE_COLORS } from './pulseConfig.js'

const VIEWS = [
    { key: 'clusters', label: '📦 Releases', desc: 'Deploys grouped by time window' },
    { key: 'unmapped', label: '⚠️ Unmapped MRs', desc: 'Shipped without Jira key' },
    { key: 'authors', label: '👤 By Assignee', desc: 'Standards rollup per author' },
    { key: 'points', label: '💰 Shipped Points', desc: 'Story points released to prod' },
    { key: 'bySprint', label: '📅 By Sprint', desc: 'Completed vs released, per sprint' },
    { key: 'aiPerformers', label: '🤖 AI Performers', desc: 'Top AI users: dwell, shipped pts, bug cohort' },
]

const BATCH_SIZE = 3

function shortRepo(path) {
    if (!path) return ''
    const parts = path.split('/')
    return parts.slice(-2).join('/')
}

function fmtDate(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
function fmtDay(iso) {
    if (!iso) return ''
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function ReleaseTracking({ excludedUserIds = [] }) {
    const [view, setView] = useState('clusters')
    const [clusters, setClusters] = useState([])
    const [unmapped, setUnmapped] = useState([])
    const [authors, setAuthors] = useState([])
    const [shippedPoints, setShippedPoints] = useState(null)
    const [diagnostics, setDiagnostics] = useState(null)
    const [diagBucket, setDiagBucket] = useState('release') // 'release' | 'week'
    const [bySprint, setBySprint] = useState(null)
    const [sprintCount, setSprintCount] = useState(8)
    const [aiPerformers, setAiPerformers] = useState(null)
    const [aiWeeks, setAiWeeks] = useState(12)
    const [aiTopN, setAiTopN] = useState(5)
    const [aiAuthorsOverride, setAiAuthorsOverride] = useState('')
    const [syncingIssues, setSyncingIssues] = useState(false)
    const [issuesSyncResult, setIssuesSyncResult] = useState(null)
    const [lastRun, setLastRun] = useState(null)
    const [loading, setLoading] = useState(true)
    const [running, setRunning] = useState(false)
    const [error, setError] = useState(null)
    const [windowHours, setWindowHours] = useState(4)
    const [days, setDays] = useState(180)
    const [progress, setProgress] = useState(null) // { total, completed: [], current: [], totals }
    const [cancelling, setCancelling] = useState(false)
    const cancelRef = useRef(false)

    const refreshAll = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const aiAuthorsParam = aiAuthorsOverride.trim()
                ? `&authors=${encodeURIComponent(aiAuthorsOverride.trim())}`
                : `&topN=${aiTopN}`
            const [c, u, a, sp, diag, lr, bs, ai] = await Promise.all([
                fetch(`/api/pulse/releases/clusters?days=${days}&windowHours=${windowHours}`).then((r) => r.json()),
                fetch(`/api/pulse/releases/unmapped?days=${days}&limit=200`).then((r) => r.json()),
                fetch(`/api/pulse/releases/by-assignee?days=${days}`).then((r) => r.json()),
                fetch(`/api/pulse/releases/shipped-points?days=${days}`).then((r) => r.json()),
                fetch(
                    `/api/pulse/releases/diagnostics?days=${days}&windowHours=${windowHours}&bucket=${diagBucket}`
                ).then((r) => r.json()),
                fetch(`/api/pulse/releases/last-run`).then((r) => r.json()),
                fetch(
                    `/api/pulse/releases/by-sprint?sprints=${sprintCount}${
                        excludedUserIds.length ? `&exclude=${encodeURIComponent(excludedUserIds.join(','))}` : ''
                    }`
                ).then((r) => r.json()),
                fetch(`/api/pulse/releases/by-author?weeks=${aiWeeks}${aiAuthorsParam}`).then((r) => r.json()),
            ])
            setClusters(c.clusters ?? [])
            setUnmapped(u.unmapped ?? [])
            setAuthors(a.authors ?? [])
            setShippedPoints(sp ?? null)
            setDiagnostics(diag ?? null)
            setLastRun(lr)
            setBySprint(bs ?? null)
            setAiPerformers(ai ?? null)
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to load release data')
        } finally {
            setLoading(false)
        }
    }, [days, windowHours, diagBucket, sprintCount, excludedUserIds, aiWeeks, aiTopN, aiAuthorsOverride])

    const runScan = useCallback(
        async (lookbackOverride) => {
            setRunning(true)
            setError(null)
            setCancelling(false)
            cancelRef.current = false

            const lookback = lookbackOverride ?? days

            try {
                // 1. Fetch the curated repo list from the server (single source of truth)
                const listRes = await fetch('/api/pulse/releases/repos')
                const listJson = await listRes.json()
                if (!listRes.ok) throw new Error(listJson?.error ?? `repos API ${listRes.status}`)
                const repos = listJson.repos ?? []
                if (repos.length === 0) throw new Error('No repos configured')

                setProgress({
                    total: repos.length,
                    completed: [],
                    current: [],
                    totals: { newDeploys: 0, newMrs: 0, examined: 0 },
                })

                // 2. Chunk into batches and scan sequentially. Each batch is one HTTP
                //    request — the server runs the repos in that batch in parallel
                //    (concurrency=3 internally). Sequential batches keep individual
                //    requests short and give per-batch progress visibility.
                for (let i = 0; i < repos.length; i += BATCH_SIZE) {
                    if (cancelRef.current) break
                    const batch = repos.slice(i, i + BATCH_SIZE)
                    setProgress((p) => (p ? { ...p, current: batch } : p))

                    const res = await fetch('/api/pulse/releases/run', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ lookbackDays: lookback, repos: batch }),
                    })
                    const json = await res.json()
                    if (!res.ok) throw new Error(json?.error ?? `run API ${res.status}`)

                    setProgress((p) => {
                        if (!p) return p
                        return {
                            ...p,
                            completed: [...p.completed, ...(json.scanned ?? [])],
                            current: [],
                            totals: {
                                newDeploys: p.totals.newDeploys + (json.newDeploys || 0),
                                newMrs: p.totals.newMrs + (json.newMrs || 0),
                                examined: p.totals.examined + (json.examined || 0),
                            },
                        }
                    })
                }

                await refreshAll()
            } catch (e) {
                setError(e instanceof Error ? e.message : 'Run failed')
            } finally {
                setRunning(false)
                // Keep progress visible briefly after completion so user can see the final state
            }
        },
        [days, refreshAll]
    )

    const cancelScan = useCallback(() => {
        cancelRef.current = true
        setCancelling(true)
    }, [])

    const syncIssues = useCallback(async () => {
        setSyncingIssues(true)
        setError(null)
        setIssuesSyncResult(null)
        try {
            const res = await fetch('/api/pulse/releases/sync-issues', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            })
            const json = await res.json()
            if (!res.ok) throw new Error(json?.error ?? `API ${res.status}`)
            setIssuesSyncResult(json)
            await refreshAll()
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Sync failed')
        } finally {
            setSyncingIssues(false)
        }
    }, [refreshAll])

    const dismissProgress = useCallback(() => setProgress(null), [])

    useEffect(() => {
        refreshAll()
    }, [refreshAll])

    const hasData = clusters.length > 0 || unmapped.length > 0 || authors.length > 0

    return (
        <div
            style={{
                background: PULSE_COLORS.cardBg,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '16px',
                }}
            >
                <div>
                    <h3 style={{ color: PULSE_COLORS.text, fontWeight: 600, fontSize: '16px' }}>
                        🚀 Release Tracking
                    </h3>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', marginTop: '2px', maxWidth: '720px' }}>
                        Anchors on GitLab <code style={{ color: PULSE_COLORS.text }}>prod_deploy</code> success events,
                        walks each deployed tag&apos;s commit range to the MRs that landed, and extracts Jira keys from
                        branch / title / description / commit messages. CR tickets and Confluence release notes are
                        cross-references — not required.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    {running && (
                        <button
                            onClick={cancelScan}
                            disabled={cancelling}
                            style={{
                                padding: '10px 16px',
                                background: 'transparent',
                                color: '#f87171',
                                fontSize: '13px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                border: '1px solid #b91c1c',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {cancelling ? 'Stopping…' : '✕ Cancel'}
                        </button>
                    )}
                    <button
                        onClick={() => runScan()}
                        disabled={running}
                        style={{
                            padding: '10px 20px',
                            background: running ? PULSE_COLORS.border : PULSE_COLORS.primary,
                            color: '#fff',
                            fontSize: '13px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            border: 'none',
                            cursor: running ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {running
                            ? '⏳ Scanning…'
                            : hasData
                              ? '↻ Incremental scan'
                              : '▶ Run first scan (slow)'}
                    </button>
                    {!running && (
                        <button
                            onClick={() => runScan(365)}
                            disabled={running}
                            title="One-shot 365-day backfill. Forces a re-probe of every tag in the last year so manual deploys that have flipped to success since the last scan get picked up. Slower."
                            style={{
                                padding: '10px 16px',
                                background: 'transparent',
                                color: PULSE_COLORS.text,
                                fontSize: '13px',
                                fontWeight: 600,
                                borderRadius: '8px',
                                border: `1px solid ${PULSE_COLORS.border}`,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            🕳 Deep scan (365d)
                        </button>
                    )}
                </div>
            </div>

            {error && (
                <div
                    style={{
                        padding: '12px',
                        marginBottom: '16px',
                        background: '#7f1d1d33',
                        border: '1px solid #b91c1c',
                        borderRadius: '8px',
                        color: '#FECDD3',
                        fontSize: '13px',
                    }}
                >
                    {error}
                </div>
            )}

            {progress && (
                <ScanProgress
                    progress={progress}
                    running={running}
                    onDismiss={running ? null : dismissProgress}
                />
            )}

            {lastRun && lastRun.last_run && (
                <div
                    style={{
                        padding: '10px 14px',
                        marginBottom: '16px',
                        background: PULSE_COLORS.bgInner,
                        border: `1px solid ${PULSE_COLORS.border}`,
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: PULSE_COLORS.textMuted,
                    }}
                >
                    Last scan: {fmtDate(lastRun.last_run)} ·{' '}
                    <strong style={{ color: PULSE_COLORS.text }}>{lastRun.repos}</strong> repos ·{' '}
                    <strong style={{ color: PULSE_COLORS.text }}>{lastRun.deploys}</strong> deploys ·{' '}
                    <strong style={{ color: PULSE_COLORS.text }}>{lastRun.total_mrs}</strong> MRs total ·{' '}
                    <strong style={{ color: '#f87171' }}>{lastRun.total_unmapped}</strong> unmapped
                </div>
            )}

            {/* View tabs + filters */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '16px',
                }}
            >
                <div
                    style={{
                        display: 'inline-flex',
                        background: PULSE_COLORS.bgInner,
                        borderRadius: '8px',
                        padding: '4px',
                        border: `1px solid ${PULSE_COLORS.border}`,
                    }}
                >
                    {VIEWS.map((v) => (
                        <button
                            key={v.key}
                            onClick={() => setView(v.key)}
                            title={v.desc}
                            style={{
                                padding: '6px 14px',
                                borderRadius: '6px',
                                fontSize: '12px',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                backgroundColor: view === v.key ? PULSE_COLORS.primary : 'transparent',
                                color: view === v.key ? '#fff' : PULSE_COLORS.textMuted,
                            }}
                        >
                            {v.label}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Lookback days
                        <input
                            type="number"
                            value={days}
                            onChange={(e) => setDays(Math.min(Math.max(Number(e.target.value) || 180, 7), 365))}
                            style={{
                                width: '60px',
                                background: PULSE_COLORS.bgInner,
                                border: `1px solid ${PULSE_COLORS.border}`,
                                color: PULSE_COLORS.text,
                                borderRadius: '4px',
                                padding: '4px 6px',
                                fontSize: '12px',
                            }}
                        />
                    </label>
                    {view === 'clusters' && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Cluster window (hrs)
                            <input
                                type="number"
                                value={windowHours}
                                onChange={(e) =>
                                    setWindowHours(Math.min(Math.max(Number(e.target.value) || 4, 1), 48))
                                }
                                style={{
                                    width: '50px',
                                    background: PULSE_COLORS.bgInner,
                                    border: `1px solid ${PULSE_COLORS.border}`,
                                    color: PULSE_COLORS.text,
                                    borderRadius: '4px',
                                    padding: '4px 6px',
                                    fontSize: '12px',
                                }}
                            />
                        </label>
                    )}
                </div>
            </div>

            {loading && !hasData && (
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '24px 0' }}>Loading…</p>
            )}
            {!loading && !hasData && !error && (
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px', margin: '24px 0' }}>
                    No scan data yet. Click the button — first scan takes 5-15 minutes (probes every recent tag in 30+
                    GitLab repos). Subsequent scans are incremental and run in under a minute.
                </p>
            )}

            {view === 'clusters' && clusters.length > 0 && <ClustersView clusters={clusters} />}
            {view === 'unmapped' && unmapped.length > 0 && <UnmappedView rows={unmapped} />}
            {view === 'authors' && authors.length > 0 && <AuthorsView rows={authors} />}
            {view === 'points' && (
                <ShippedPointsView
                    data={shippedPoints}
                    diagnostics={diagnostics}
                    bucket={diagBucket}
                    onBucketChange={setDiagBucket}
                    syncing={syncingIssues}
                    onSync={syncIssues}
                    syncResult={issuesSyncResult}
                />
            )}
            {view === 'bySprint' && (
                <BySprintView
                    data={bySprint}
                    sprintCount={sprintCount}
                    onSprintCountChange={setSprintCount}
                />
            )}
            {view === 'aiPerformers' && (
                <AiPerformersView
                    data={aiPerformers}
                    weeks={aiWeeks}
                    onWeeksChange={setAiWeeks}
                    topN={aiTopN}
                    onTopNChange={setAiTopN}
                    authorsOverride={aiAuthorsOverride}
                    onAuthorsOverrideChange={setAiAuthorsOverride}
                    onRefresh={refreshAll}
                />
            )}
        </div>
    )
}

function ScanProgress({ progress, running, onDismiss }) {
    const { total, completed, current, totals } = progress
    const pct = total > 0 ? Math.round((completed.length / total) * 100) : 0
    const recent = completed.slice(-8).reverse() // newest first, cap to 8 visible

    return (
        <div
            style={{
                padding: '14px 16px',
                marginBottom: '16px',
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '8px',
                fontSize: '12px',
                color: PULSE_COLORS.text,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    marginBottom: '10px',
                }}
            >
                <div style={{ fontWeight: 600, fontSize: '13px' }}>
                    {running ? '⏳ Scanning' : '✓ Scan complete'} — {completed.length} / {total} repos
                    <span style={{ color: PULSE_COLORS.textMuted, fontWeight: 400, marginLeft: '8px' }}>
                        ({totals.examined} tags probed · {totals.newDeploys} new deploys · {totals.newMrs} new MRs)
                    </span>
                </div>
                {onDismiss && (
                    <button
                        onClick={onDismiss}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: PULSE_COLORS.textMuted,
                            cursor: 'pointer',
                            fontSize: '14px',
                            padding: '0 4px',
                        }}
                        title="Dismiss"
                    >
                        ✕
                    </button>
                )}
            </div>

            <div
                style={{
                    height: '6px',
                    background: PULSE_COLORS.border,
                    borderRadius: '3px',
                    overflow: 'hidden',
                    marginBottom: '10px',
                }}
            >
                <div
                    style={{
                        height: '100%',
                        width: `${pct}%`,
                        background: PULSE_COLORS.primary,
                        transition: 'width 0.3s ease',
                    }}
                />
            </div>

            {current.length > 0 && (
                <div style={{ color: PULSE_COLORS.textMuted, marginBottom: '8px' }}>
                    Now scanning: {current.map(shortRepo).join(' · ')}
                </div>
            )}

            {recent.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', fontSize: '11px' }}>
                    {recent.map((r) => (
                        <div
                            key={r.repo}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '12px',
                                color: r.error ? '#f87171' : PULSE_COLORS.textMuted,
                            }}
                        >
                            <span style={{ fontFamily: 'monospace' }}>{shortRepo(r.repo)}</span>
                            <span>
                                {r.error
                                    ? `error: ${r.error}`
                                    : `${r.examined} probed · ${r.newDeploys} deploys · ${r.newMrs} MRs · ${(r.durationMs / 1000).toFixed(1)}s`}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

function ClustersView({ clusters }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {clusters.map((c, i) => (
                <div
                    key={i}
                    style={{
                        padding: '14px',
                        background: PULSE_COLORS.bgInner,
                        border: `1px solid ${PULSE_COLORS.border}`,
                        borderRadius: '8px',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '8px',
                            marginBottom: '8px',
                        }}
                    >
                        <div style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '14px' }}>
                            {fmtDate(c.first_deployed_at)}
                            {c.first_deployed_at !== c.last_deployed_at && ` → ${fmtDate(c.last_deployed_at)}`}
                        </div>
                        <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                            <span style={{ color: PULSE_COLORS.textMuted }}>
                                <strong style={{ color: PULSE_COLORS.text }}>{c.tags.length}</strong> deploys
                            </span>
                            <span style={{ color: PULSE_COLORS.textMuted }}>
                                <strong style={{ color: PULSE_COLORS.text }}>{c.mr_count}</strong> MRs
                            </span>
                            {c.unmapped_count > 0 && (
                                <span style={{ color: '#f87171' }}>
                                    <strong>{c.unmapped_count}</strong> unmapped
                                </span>
                            )}
                            <span style={{ color: PULSE_COLORS.textMuted }}>
                                <strong style={{ color: PULSE_COLORS.text }}>
                                    {(c.jira_keys ?? []).length}
                                </strong>{' '}
                                Jira keys
                            </span>
                        </div>
                    </div>
                    <div style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginBottom: '6px' }}>
                        Repos: {c.tags.join(', ')}
                    </div>
                    {c.jira_keys && c.jira_keys.length > 0 && (
                        <details>
                            <summary
                                style={{
                                    fontSize: '11px',
                                    color: PULSE_COLORS.textMuted,
                                    cursor: 'pointer',
                                    marginBottom: '4px',
                                }}
                            >
                                Jira keys ({c.jira_keys.length})
                            </summary>
                            <div
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: '4px',
                                    marginTop: '6px',
                                    fontSize: '11px',
                                }}
                            >
                                {c.jira_keys.map((k) => (
                                    <a
                                        key={k}
                                        href={`https://bstock.atlassian.net/browse/${k}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{
                                            padding: '2px 6px',
                                            background: PULSE_COLORS.cardBg,
                                            border: `1px solid ${PULSE_COLORS.border}`,
                                            borderRadius: '4px',
                                            color: PULSE_COLORS.primary,
                                            textDecoration: 'none',
                                        }}
                                    >
                                        {k}
                                    </a>
                                ))}
                            </div>
                        </details>
                    )}
                    {c.top_authors && c.top_authors.length > 0 && (
                        <div style={{ marginTop: '6px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                            Contributors:{' '}
                            {c.top_authors
                                .filter((a) => a.author_username)
                                .map((a) => `${a.author_username} (${a.n})`)
                                .join(', ')}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}

function UnmappedView({ rows }) {
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Merged</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Author</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Repo</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>MR</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Branch</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Changes</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                            <td style={tdStyle}>{fmtDay(r.merged_at)}</td>
                            <td style={tdStyle}>
                                <span style={{ color: PULSE_COLORS.text, fontWeight: 600 }}>{r.author_name}</span>
                                <br />
                                <span style={{ color: PULSE_COLORS.textMuted, fontSize: '10px' }}>
                                    {r.author_username}
                                </span>
                            </td>
                            <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted }}>
                                {r.repo_path.split('/').slice(-2).join('/')}
                            </td>
                            <td style={tdStyle}>
                                <a
                                    href={r.web_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: PULSE_COLORS.primary, textDecoration: 'none' }}
                                >
                                    !{r.mr_iid}
                                </a>
                                <br />
                                <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>{r.title}</span>
                            </td>
                            <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontFamily: 'monospace', fontSize: '11px' }}>
                                {r.source_branch}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: PULSE_COLORS.textMuted }}>
                                {r.changes_count ?? '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginTop: '12px' }}>
                Unmapped = no Jira key found in source_branch, MR title, MR description, or any commit message in the
                MR. If you see a key in the data above, it means our parser missed it — please flag so the regex can be
                extended.
            </p>
        </div>
    )
}

function AuthorsView({ rows }) {
    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Author</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>MRs to prod</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Unmapped</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Unmapped %</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Most recent unmapped</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                            <td style={tdStyle}>
                                <span style={{ color: PULSE_COLORS.text, fontWeight: 600 }}>{r.author_name}</span>
                                <br />
                                <span style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                    {r.author_username}
                                </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: PULSE_COLORS.text }}>{r.total_mrs}</td>
                            <td
                                style={{
                                    ...tdStyle,
                                    textAlign: 'right',
                                    color: r.unmapped_mrs > 0 ? '#f87171' : PULSE_COLORS.textMuted,
                                    fontWeight: 600,
                                }}
                            >
                                {r.unmapped_mrs}
                            </td>
                            <td
                                style={{
                                    ...tdStyle,
                                    textAlign: 'right',
                                    color: r.unmapped_pct > 0.2 ? '#f87171' : r.unmapped_pct > 0 ? '#fbbf24' : PULSE_COLORS.textMuted,
                                    fontWeight: r.unmapped_pct > 0 ? 600 : 400,
                                }}
                            >
                                {r.total_mrs > 0 ? `${(r.unmapped_pct * 100).toFixed(0)}%` : '—'}
                            </td>
                            <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                {r.most_recent_unmapped_at ? fmtDay(r.most_recent_unmapped_at) : '—'}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <p style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginTop: '12px' }}>
                Sorted by unmapped count (highest first). Click an author into the Unmapped MRs view to drill in.
            </p>
        </div>
    )
}

const thStyle = {
    padding: '8px 12px 8px 0',
    color: PULSE_COLORS.textMuted,
    fontWeight: 500,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
}
const tdStyle = {
    padding: '8px 12px 8px 0',
    color: PULSE_COLORS.text,
    verticalAlign: 'top',
}

function ShippedPointsView({ data, diagnostics, bucket, onBucketChange, syncing, onSync, syncResult }) {
    const hasIssues = data?.totals?.issues > 0
    const missingCache = data?.keys_missing_cache ?? 0
    const needsSync = !hasIssues || missingCache > 0

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    flexWrap: 'wrap',
                }}
            >
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', maxWidth: '720px', margin: 0 }}>
                    Story points that landed in production. Computed by joining MR-borne Jira keys to issue details
                    (issue type, points, AI label). Epics are expanded to their child stories. Each story is counted once
                    at its first prod deploy.
                </p>
                <button
                    onClick={onSync}
                    disabled={syncing}
                    style={{
                        padding: '8px 14px',
                        background: syncing ? PULSE_COLORS.border : PULSE_COLORS.primary,
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600,
                        borderRadius: '6px',
                        border: 'none',
                        cursor: syncing ? 'not-allowed' : 'pointer',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {syncing
                        ? '⏳ Fetching Jira…'
                        : needsSync
                          ? '▶ Sync Jira issues'
                          : '↻ Re-sync Jira issues'}
                </button>
            </div>

            {syncResult && (
                <div
                    style={{
                        padding: '10px 14px',
                        background: PULSE_COLORS.bgInner,
                        border: `1px solid ${PULSE_COLORS.border}`,
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: PULSE_COLORS.textMuted,
                    }}
                >
                    Last sync: fetched {syncResult.fetched} new issue(s) · expanded{' '}
                    {syncResult.epics_expanded} epic(s) · cache now has {syncResult.cache?.total} issue(s)
                </div>
            )}

            {!hasIssues && (
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>
                    No shipped-points data yet. Click <strong>Sync Jira issues</strong> to fetch story-point details for
                    every Jira key carried by an in-scope MR. First sync takes ~30s for ~700 keys.
                </p>
            )}

            {hasIssues && (
                <>
                    <SummaryCards totals={data.totals} />
                    {diagnostics && diagnostics.clusters && diagnostics.clusters.length > 0 && (
                        <ReleaseDiagnosticsChart
                            clusters={diagnostics.clusters}
                            bucket={bucket}
                            onBucketChange={onBucketChange}
                            weeklySummary={diagnostics.weekly_summary}
                        />
                    )}
                    <SplitTable title="By issue type" rows={data.by_type} keyField="issuetype" />
                    <SplitTable title="By pod" rows={data.by_pod} keyField="project" />
                    {missingCache > 0 && (
                        <p style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                            {missingCache} MR-borne key(s) not yet in cache — click Sync to fill them in.
                        </p>
                    )}
                </>
            )}
        </div>
    )
}

// Mirrors the SprintDiagnosticsChart on the velocity tab — overlaid lines,
// dual Y-axis (points on left, counts on right), rich tooltip with delta-vs-previous.
// X-axis is chronological release clusters (R1, R2, …) so users can see the
// per-release shape of shipped work.
const RELEASE_METRICS = [
    {
        key: 'points',
        label: 'Points',
        emoji: '💰',
        color: '#4ade80',
        yAxis: 'left',
        higherIsBetter: true,
        desc: 'Total story points landed in production this cluster (epic-expanded, deduped first-ship).',
    },
    {
        key: 'ai_points',
        label: 'AI Points',
        emoji: '🤖',
        color: '#a78bfa',
        yAxis: 'left',
        strokeDasharray: '4 2',
        higherIsBetter: null,
        desc: 'Subset of points carrying the AICompleted label.',
    },
    {
        key: 'story_count',
        label: 'Stories',
        emoji: '📋',
        color: '#60a5fa',
        yAxis: 'right',
        higherIsBetter: true,
        desc: 'Story-type issues shipped.',
    },
    {
        key: 'bug_count',
        label: 'Bugs',
        emoji: '🐛',
        color: '#f87171',
        yAxis: 'right',
        strokeDasharray: '3 3',
        higherIsBetter: null,
        desc: 'Bug-type issues shipped.',
    },
    {
        key: 'task_count',
        label: 'Tasks',
        emoji: '🛠️',
        color: '#fbbf24',
        yAxis: 'right',
        strokeDasharray: '2 4',
        higherIsBetter: null,
        desc: 'Task-type issues shipped.',
    },
    {
        key: 'avg_size',
        label: 'Avg Size',
        emoji: '📐',
        color: '#22d3ee',
        yAxis: 'right',
        strokeDasharray: '5 3',
        higherIsBetter: null,
        desc: 'Points ÷ issues for this cluster — rising means larger work units.',
    },
    {
        key: 'lag_days',
        label: 'Lag (days)',
        emoji: '⏱️',
        color: '#fb7185',
        yAxis: 'right',
        strokeDasharray: '6 2',
        higherIsBetter: false, // lower lag = work shipping closer to completion
        desc: 'Point-weighted avg days from Jira resolutiondate to first prod deploy. Rising = work sitting longer between done and shipped.',
    },
]

function ReleaseDiagnosticsChart({ clusters, bucket, onBucketChange, weeklySummary }) {
    // Oldest → newest for the X axis. Endpoint returns newest-first ordering of
    // raw deploys but our clusters[] already has idx in chronological order;
    // sort defensively by first_deployed_at.
    const data = [...clusters].sort(
        (a, b) => new Date(a.first_deployed_at).getTime() - new Date(b.first_deployed_at).getTime()
    )

    const DiagnosticsTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        const cluster = payload[0]?.payload
        const idx = data.findIndex((d) => d.label === cluster?.label)
        const prev = idx > 0 ? data[idx - 1] : null

        return (
            <div
                style={{
                    background: PULSE_COLORS.cardBg,
                    border: `1px solid ${PULSE_COLORS.border}`,
                    borderRadius: '10px',
                    padding: '12px 14px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                    minWidth: '240px',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '2px' }}>
                    <span style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '13px' }}>{label}</span>
                    {prev && <span style={{ color: PULSE_COLORS.textMuted, fontSize: '10px' }}>vs {prev.label}</span>}
                </div>
                <div style={{ color: PULSE_COLORS.textMuted, fontSize: '10px', marginBottom: '2px' }}>
                    {cluster?.seq_label}
                    {cluster?.release_name ? '' : ' · no Jira fixVersion on shipped issues'}
                    {cluster?.first_deployed_at ? ' · ' : ''}
                    {fmtDay(cluster?.first_deployed_at)}
                    {cluster?.last_deployed_at && cluster.last_deployed_at !== cluster.first_deployed_at
                        ? ` → ${fmtDay(cluster.last_deployed_at)}`
                        : ''}
                    {cluster?.repo_count != null ? ` · ${cluster.repo_count} repo${cluster.repo_count === 1 ? '' : 's'}` : ''}
                </div>
                {cluster?.release_breakdown && cluster.release_breakdown.length > 1 && (
                    <div style={{ color: PULSE_COLORS.textMuted, fontSize: '10px', marginBottom: '8px' }}>
                        mix: {cluster.release_breakdown.map((r) => r.name).join(' · ')}
                    </div>
                )}
                {(!cluster?.release_breakdown || cluster.release_breakdown.length <= 1) && (
                    <div style={{ marginBottom: '8px' }} />
                )}
                {payload.map((entry) => {
                    const metric = RELEASE_METRICS.find((m) => m.key === entry.dataKey)
                    const cur = entry.value
                    const prv = prev != null ? prev[entry.dataKey] : null
                    const delta = prv != null ? Number((cur - prv).toFixed(1)) : null
                    let deltaColor = PULSE_COLORS.textMuted
                    if (delta !== null && delta !== 0 && metric?.higherIsBetter != null) {
                        const improved = metric.higherIsBetter ? delta > 0 : delta < 0
                        deltaColor = improved ? '#4ade80' : '#f87171'
                    }
                    const arrow = delta === null ? null : delta > 0 ? '↑' : delta < 0 ? '↓' : '→'
                    const sign = delta > 0 ? '+' : ''
                    return (
                        <div
                            key={entry.dataKey}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: '12px',
                                marginBottom: '4px',
                            }}
                        >
                            <span style={{ color: entry.color, fontSize: '12px' }}>{entry.name}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '12px' }}>{cur}</span>
                                {arrow && (
                                    <span style={{ color: deltaColor, fontSize: '11px', minWidth: '36px', textAlign: 'right' }}>
                                        {arrow}
                                        {sign}
                                        {Math.abs(delta)}
                                    </span>
                                )}
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    return (
        <div
            style={{
                background: PULSE_COLORS.bgInner,
                borderRadius: '12px',
                padding: '20px',
                border: `1px solid ${PULSE_COLORS.border}`,
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '12px',
                    marginBottom: '12px',
                    flexWrap: 'wrap',
                }}
            >
                <div>
                    <h4 style={{ color: PULSE_COLORS.text, fontSize: '14px', fontWeight: 600, margin: 0 }}>
                        🔬 Release Diagnostics
                    </h4>
                    <p style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', margin: '2px 0 0' }}>
                        {bucket === 'week'
                            ? 'Story points released per week — empty weeks are shown to make cadence visible'
                            : 'What landed in each release cluster — overlay metrics to see how the shape changes over time'}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    {weeklySummary && weeklySummary.weeks_counted > 0 && (
                        <div
                            style={{
                                padding: '6px 12px',
                                background: '#4ade8018',
                                border: '1px solid #4ade8050',
                                borderRadius: '8px',
                                fontSize: '11px',
                                color: PULSE_COLORS.textMuted,
                                lineHeight: 1.3,
                            }}
                            title={`Trailing ${weeklySummary.weeks_counted}-week average. Excludes the current (in-progress) week.`}
                        >
                            <div style={{ color: '#4ade80', fontWeight: 700, fontSize: '13px' }}>
                                {weeklySummary.avg_points_per_week} pts/wk
                            </div>
                            <div>
                                {weeklySummary.avg_issues_per_week} issues/wk · trailing {weeklySummary.weeks_counted}w
                            </div>
                        </div>
                    )}
                    <div
                        style={{
                            display: 'inline-flex',
                            background: PULSE_COLORS.cardBg,
                            borderRadius: '6px',
                            padding: '3px',
                            border: `1px solid ${PULSE_COLORS.border}`,
                        }}
                    >
                        {[
                            { key: 'release', label: 'Per release' },
                            { key: 'week', label: 'Weekly' },
                        ].map((b) => (
                            <button
                                key={b.key}
                                onClick={() => onBucketChange(b.key)}
                                style={{
                                    padding: '5px 10px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    border: 'none',
                                    cursor: 'pointer',
                                    backgroundColor: bucket === b.key ? PULSE_COLORS.primary : 'transparent',
                                    color: bucket === b.key ? '#fff' : PULSE_COLORS.textMuted,
                                }}
                            >
                                {b.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
                {RELEASE_METRICS.map((m) => (
                    <div
                        key={m.key}
                        title={m.desc}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            background: `${m.color}18`,
                            border: `1px solid ${m.color}50`,
                            borderRadius: '6px',
                            padding: '4px 9px',
                        }}
                    >
                        <span style={{ fontSize: '13px' }}>{m.emoji}</span>
                        <span style={{ color: m.color, fontSize: '11px', fontWeight: 700 }}>{m.label}</span>
                    </div>
                ))}
            </div>

            <div
                style={{
                    display: 'flex',
                    gap: '14px',
                    marginBottom: '4px',
                    fontSize: '10px',
                    color: PULSE_COLORS.textMuted,
                }}
            >
                <span>
                    ← Left axis: <span style={{ color: '#4ade80' }}>Points</span>,{' '}
                    <span style={{ color: '#a78bfa' }}>AI Points</span>
                </span>
                <span>
                    Right axis →: <span style={{ color: '#60a5fa' }}>Stories</span>,{' '}
                    <span style={{ color: '#f87171' }}>Bugs</span>,{' '}
                    <span style={{ color: '#fbbf24' }}>Tasks</span>,{' '}
                    <span style={{ color: '#22d3ee' }}>Avg Size</span>,{' '}
                    <span style={{ color: '#fb7185' }}>Lag</span>
                </span>
            </div>

            <ResponsiveContainer width="100%" height={300}>
                <LineChart data={data} margin={{ top: 5, right: 40, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={PULSE_COLORS.border} />
                    <XAxis dataKey="label" stroke={PULSE_COLORS.textMuted} fontSize={11} />
                    <YAxis yAxisId="left" stroke={PULSE_COLORS.textMuted} fontSize={11} domain={[0, 'auto']} tickCount={6} />
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        stroke={PULSE_COLORS.textMuted}
                        fontSize={11}
                        domain={[0, 'auto']}
                        tickCount={6}
                    />
                    <Tooltip content={<DiagnosticsTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px', color: PULSE_COLORS.textMuted, paddingTop: '8px' }} />
                    {RELEASE_METRICS.map((m) => (
                        <Line
                            key={m.key}
                            yAxisId={m.yAxis}
                            type="monotone"
                            dataKey={m.key}
                            stroke={m.color}
                            strokeWidth={2}
                            strokeDasharray={m.strokeDasharray}
                            dot={{ fill: m.color, r: 3, strokeWidth: 0 }}
                            activeDot={{ r: 5, fill: m.color }}
                            name={`${m.emoji} ${m.label}`}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </div>
    )
}

function SummaryCards({ totals }) {
    const cards = [
        { label: 'Issues shipped', value: totals.issues, accent: PULSE_COLORS.text },
        { label: 'Story points', value: totals.points, accent: PULSE_COLORS.primary },
        { label: 'AI-labeled issues', value: totals.ai_issues, accent: '#a78bfa' },
        { label: 'AI-labeled points', value: totals.ai_points, accent: '#a78bfa' },
    ]
    return (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {cards.map((c) => (
                <div
                    key={c.label}
                    style={{
                        flex: '1 1 160px',
                        padding: '14px 16px',
                        background: PULSE_COLORS.bgInner,
                        border: `1px solid ${PULSE_COLORS.border}`,
                        borderRadius: '8px',
                    }}
                >
                    <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        {c.label}
                    </div>
                    <div style={{ color: c.accent, fontSize: '22px', fontWeight: 700, marginTop: '4px' }}>
                        {c.value}
                    </div>
                </div>
            ))}
        </div>
    )
}

function SplitTable({ title, rows, keyField }) {
    if (!rows || rows.length === 0) return null
    const totalPoints = rows.reduce((s, r) => s + (r.points || 0), 0)
    return (
        <div>
            <h4
                style={{
                    color: PULSE_COLORS.text,
                    fontWeight: 600,
                    fontSize: '13px',
                    marginBottom: '6px',
                    marginTop: 0,
                }}
            >
                {title}
            </h4>
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}`, textAlign: 'left' }}>
                        <th style={thStyle}>{keyField === 'issuetype' ? 'Type' : 'Pod'}</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Issues</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>% of points</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>AI points</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r[keyField]} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                            <td style={tdStyle}>{r[keyField]}</td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{r.issues}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{r.points}</td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: PULSE_COLORS.textMuted }}>
                                {totalPoints > 0 ? Math.round((r.points / totalPoints) * 100) : 0}%
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right', color: '#a78bfa' }}>{r.ai_points}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// Per-sprint completed-vs-released view. Expects payload from
// /api/pulse/releases/by-sprint: sprints[] with .completed / .shipped /
// .unmapped_regex_gap / .not_yet_shipped / .releases[] / shipped_issues[] /
// unmapped_issues[] / not_yet_shipped_issues[].
function BySprintView({ data, sprintCount, onSprintCountChange }) {
    const [expanded, setExpanded] = useState(null) // sprint_num
    const sprints = data?.sprints ?? []
    const note = data?.note ?? null

    if (note) {
        return (
            <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>{note}</p>
        )
    }
    if (!sprints.length) {
        return (
            <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>
                No sprint history yet. Refresh the velocity tab to populate the snapshot.
            </p>
        )
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '12px',
                }}
            >
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', maxWidth: '720px', margin: 0 }}>
                    For each closed sprint: how many of the stories completed in that 2-week window have
                    landed in production, grouped by release version, with lag (days from Jira resolution to
                    first prod deploy). Stories not covered by the MR-tag mechanism are split using a{' '}
                    <strong style={{ color: PULSE_COLORS.text }}>per-pod</strong> cutoff —{' '}
                    <strong style={{ color: PULSE_COLORS.text }}>likely not yet shipped</strong> (the
                    story&apos;s pod hasn&apos;t had a successful prod deploy since the story was resolved)
                    vs.{' '}
                    <strong style={{ color: PULSE_COLORS.text }}>regex gap</strong> (the pod HAS deployed
                    since, but no MR in our cache carries the key).
                </p>
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        color: PULSE_COLORS.textMuted,
                        fontSize: '11px',
                    }}
                >
                    Sprints
                    <input
                        type="number"
                        value={sprintCount}
                        onChange={(e) =>
                            onSprintCountChange(Math.min(Math.max(Number(e.target.value) || 8, 1), 30))
                        }
                        style={{
                            width: '50px',
                            background: PULSE_COLORS.bgInner,
                            border: `1px solid ${PULSE_COLORS.border}`,
                            color: PULSE_COLORS.text,
                            borderRadius: '4px',
                            padding: '4px 6px',
                            fontSize: '12px',
                        }}
                    />
                </label>
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                            <th style={{ ...thStyle, textAlign: 'left' }}>Sprint</th>
                            <th style={{ ...thStyle, textAlign: 'left' }}>Window</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Stories</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Shipped pts</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Coverage</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Releases</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Not yet shipped</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Regex gap</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Non-code</th>
                            <th style={{ ...thStyle, textAlign: 'right' }} />
                        </tr>
                    </thead>
                    <tbody>
                        {sprints.map((s) => {
                            const isOpen = expanded === s.sprint_num
                            const coverColor =
                                s.shipped.coverage_pct >= 80
                                    ? '#4ade80'
                                    : s.shipped.coverage_pct >= 40
                                      ? '#fbbf24'
                                      : '#f87171'
                            return (
                                <Fragment key={s.sprint_num}>
                                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>
                                            {s.name || `Sprint ${s.sprint_num}`}
                                        </td>
                                        <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                            {fmtDay(s.start_date)} → {fmtDay(s.end_date)}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{s.completed.stories}</td>
                                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>
                                            {s.completed.points}
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>{s.shipped.points}</td>
                                        <td
                                            style={{
                                                ...tdStyle,
                                                textAlign: 'right',
                                                color: coverColor,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {s.shipped.coverage_pct}%
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right', color: PULSE_COLORS.textMuted }}>
                                            {s.releases.length}
                                        </td>
                                        <td
                                            style={{
                                                ...tdStyle,
                                                textAlign: 'right',
                                                color:
                                                    s.not_yet_shipped.stories > 0
                                                        ? PULSE_COLORS.text
                                                        : PULSE_COLORS.textMuted,
                                            }}
                                        >
                                            {s.not_yet_shipped.stories} ({s.not_yet_shipped.points})
                                        </td>
                                        <td
                                            style={{
                                                ...tdStyle,
                                                textAlign: 'right',
                                                color:
                                                    s.unmapped_regex_gap.stories > 0 ? '#f87171' : PULSE_COLORS.textMuted,
                                            }}
                                        >
                                            {s.unmapped_regex_gap.stories} ({s.unmapped_regex_gap.points})
                                        </td>
                                        <td
                                            style={{
                                                ...tdStyle,
                                                textAlign: 'right',
                                                color:
                                                    s.non_code?.stories > 0 ? '#a78bfa' : PULSE_COLORS.textMuted,
                                            }}
                                            title={`Tickets the classifier marked as non-deployable work — ${
                                                s.non_code?.by_category
                                                    ? Object.entries(s.non_code.by_category)
                                                          .filter(([, v]) => v.stories > 0)
                                                          .map(([c, v]) => `${c} ${v.stories}`)
                                                          .join(', ')
                                                    : ''
                                            }`}
                                        >
                                            {s.non_code?.stories ?? 0} ({s.non_code?.points ?? 0})
                                        </td>
                                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                                            <button
                                                onClick={() => setExpanded(isOpen ? null : s.sprint_num)}
                                                style={{
                                                    background: 'transparent',
                                                    border: `1px solid ${PULSE_COLORS.border}`,
                                                    color: PULSE_COLORS.text,
                                                    borderRadius: '4px',
                                                    padding: '2px 8px',
                                                    fontSize: '11px',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {isOpen ? '▼' : '▶'}
                                            </button>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr style={{ background: PULSE_COLORS.bgInner }}>
                                            <td colSpan={11} style={{ padding: '12px 16px' }}>
                                                <BySprintDetail sprint={s} />
                                            </td>
                                        </tr>
                                    )}
                                </Fragment>
                            )
                        })}
                    </tbody>
                </table>
            </div>
            <p style={{ fontSize: '11px', color: PULSE_COLORS.textMuted, marginTop: '4px' }}>
                Note: a story carrying its epic key on the MR (rather than its own key) is still counted as
                shipped — that&apos;s the existing epic-expansion convention. Stories with no resolution date
                contribute to counts but not lag.
            </p>
        </div>
    )
}

function BySprintDetail({ sprint }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {sprint.releases.length > 0 && (
                <div>
                    <div
                        style={{
                            color: PULSE_COLORS.textMuted,
                            fontSize: '11px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: '6px',
                        }}
                    >
                        Shipped by release · {sprint.shipped.stories} stories · {sprint.shipped.points} pts
                    </div>
                    <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                                <th style={{ ...thStyle, textAlign: 'left' }}>Release</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>First ship</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Stories</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>AI pts</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Lag p50</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Lag p90</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sprint.releases.map((r) => (
                                <tr key={r.release_name} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                                    <td style={tdStyle}>
                                        <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                                            {r.release_name}
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                        {fmtDay(r.first_ship)}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{r.stories}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{r.points}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#a78bfa' }}>{r.ai_points}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        {r.lag_p50_days != null ? `${r.lag_p50_days}d` : '—'}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: PULSE_COLORS.textMuted }}>
                                        {r.lag_p90_days != null ? `${r.lag_p90_days}d` : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {sprint.not_yet_shipped_issues.length > 0 && (
                <IssueList
                    title={`Likely not yet shipped (${sprint.not_yet_shipped.stories} stories · ${sprint.not_yet_shipped.points} pts)`}
                    accentColor={PULSE_COLORS.textMuted}
                    issues={sprint.not_yet_shipped_issues}
                    hint="Resolved after the most recent prod deploy we've recorded — almost certainly just waiting on the next release."
                />
            )}

            {sprint.unmapped_issues.length > 0 && (
                <IssueList
                    title={`Regex gap (${sprint.unmapped_regex_gap.stories} stories · ${sprint.unmapped_regex_gap.points} pts)`}
                    accentColor="#f87171"
                    issues={sprint.unmapped_issues}
                    hint="A prod deploy has happened since these were resolved, but no MR carries the key. Either the branch/title/description/commits don't reference the Jira key, or the work didn't ship as code."
                />
            )}

            {(sprint.non_code_issues || []).length > 0 && (
                <IssueList
                    title={`Non-code (${sprint.non_code.stories} stories · ${sprint.non_code.points} pts · ${
                        Object.entries(sprint.non_code.by_category || {})
                            .filter(([, v]) => v.stories > 0)
                            .map(([c, v]) => `${c} ${v.stories}`)
                            .join(' · ') || '—'
                    })`}
                    accentColor="#a78bfa"
                    issues={sprint.non_code_issues}
                    hint="Tickets the classifier marked as non-deployable work (spike / support / docs / research / design / chore). Excluded from coverage % since the ship/no-ship framing doesn't apply. If a row looks wrong, refine the rules in server/release-tracking.js → NON_CODE_RULES."
                    showCategory
                />
            )}

            {sprint.shipped_issues.length > 0 && (
                <details>
                    <summary
                        style={{
                            color: PULSE_COLORS.textMuted,
                            fontSize: '11px',
                            cursor: 'pointer',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                        }}
                    >
                        Shipped stories ({sprint.shipped_issues.length})
                    </summary>
                    <div style={{ marginTop: '8px' }}>
                        <IssueList issues={sprint.shipped_issues} mode="shipped" />
                    </div>
                </details>
            )}
        </div>
    )
}

function IssueList({ title, hint, issues, accentColor, mode, showCategory }) {
    return (
        <div>
            {title && (
                <div
                    style={{
                        color: accentColor || PULSE_COLORS.textMuted,
                        fontSize: '11px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        marginBottom: '4px',
                        fontWeight: 600,
                    }}
                >
                    {title}
                </div>
            )}
            {hint && (
                <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', marginBottom: '6px' }}>
                    {hint}
                </div>
            )}
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                <thead>
                    <tr style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Key</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Type</th>
                        <th style={{ ...thStyle, textAlign: 'left' }}>Assignee</th>
                        <th style={{ ...thStyle, textAlign: 'right' }}>Points</th>
                        {mode === 'shipped' ? (
                            <>
                                <th style={{ ...thStyle, textAlign: 'left' }}>Release</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Lag</th>
                            </>
                        ) : (
                            <th style={{ ...thStyle, textAlign: 'left' }}>Resolved</th>
                        )}
                        <th style={{ ...thStyle, textAlign: 'left' }}>Summary</th>
                    </tr>
                </thead>
                <tbody>
                    {issues.map((i) => (
                        <tr key={i.key} style={{ borderBottom: `1px solid ${PULSE_COLORS.border}` }}>
                            <td style={tdStyle}>
                                <a
                                    href={`https://bstock.atlassian.net/browse/${i.key}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: PULSE_COLORS.primary, textDecoration: 'none' }}
                                >
                                    {i.key}
                                </a>
                                {i.via_epic && (
                                    <span
                                        title="Inherited shipping date from parent epic"
                                        style={{ color: PULSE_COLORS.textMuted, fontSize: '10px', marginLeft: '4px' }}
                                    >
                                        (via epic)
                                    </span>
                                )}
                                {showCategory && i.category && (
                                    <span
                                        style={{
                                            color: '#a78bfa',
                                            fontSize: '10px',
                                            marginLeft: '4px',
                                            padding: '1px 5px',
                                            border: '1px solid #a78bfa55',
                                            borderRadius: '4px',
                                            background: '#a78bfa11',
                                        }}
                                    >
                                        {i.category}
                                    </span>
                                )}
                                {i.is_ai && (
                                    <span style={{ color: '#a78bfa', fontSize: '10px', marginLeft: '4px' }}>🤖</span>
                                )}
                            </td>
                            <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                {i.type || '—'}
                            </td>
                            <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                {i.assignee || '—'}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'right' }}>{i.points}</td>
                            {mode === 'shipped' ? (
                                <>
                                    <td
                                        style={{
                                            ...tdStyle,
                                            color: PULSE_COLORS.textMuted,
                                            fontFamily: 'monospace',
                                            fontSize: '11px',
                                        }}
                                    >
                                        {i.release_name}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        {i.lag_days != null ? `${i.lag_days}d` : '—'}
                                    </td>
                                </>
                            ) : (
                                <td style={{ ...tdStyle, color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                                    {i.resolved ? fmtDay(i.resolved) : '—'}
                                </td>
                            )}
                            <td
                                style={{
                                    ...tdStyle,
                                    color: PULSE_COLORS.text,
                                    maxWidth: '420px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                                title={i.summary || ''}
                            >
                                {i.summary || ''}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

// AI Performers — per-developer view showing whether AI-velocity comes with
// quality cost. Data: GET /api/pulse/releases/by-author returns authors[] with
// engineering_dwell_days, pipeline_dwell_days, shipped_points, ai_share_pct,
// bug_cohort (same-repo + N-day window — see server), and weekly[] shipped points.
function AiPerformersView({ data, weeks, onWeeksChange, topN, onTopNChange, authorsOverride, onAuthorsOverrideChange, onRefresh }) {
    const authors = data?.authors ?? []
    const note = data?.note
    const bugWindow = data?.bug_window_days ?? 14
    const coverage = data?.blame_coverage ?? null
    const [blameStatus, setBlameStatus] = useState(null) // { running, summary, error }

    const runBlameScan = async (days = 30, limit = 50) => {
        setBlameStatus({ running: true })
        try {
            const r = await fetch(`/api/pulse/releases/scan-blame?days=${days}&limit=${limit}`, { method: 'POST' })
            const j = await r.json()
            if (!r.ok) throw new Error(j?.error ?? `scan-blame ${r.status}`)
            setBlameStatus({ running: false, summary: j })
            if (onRefresh) await onRefresh()
        } catch (e) {
            setBlameStatus({ running: false, error: e.message })
        }
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    flexWrap: 'wrap',
                    gap: '12px',
                }}
            >
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '12px', maxWidth: '720px', margin: 0 }}>
                    Per-developer view of <strong style={{ color: PULSE_COLORS.text }}>engineering dwell</strong>{' '}
                    (MR open → merged), <strong style={{ color: PULSE_COLORS.text }}>pipeline dwell</strong>{' '}
                    (merged → first prod deploy), <strong style={{ color: PULSE_COLORS.text }}>shipped points</strong>,
                    AI share, plus two bug-attribution proxies: <strong style={{ color: PULSE_COLORS.text }}>same-repo
                    {bugWindow}d follow-on</strong> (Bug Jira keys shipped to the same repo within {bugWindow} days)
                    and <strong style={{ color: PULSE_COLORS.text }}>blame attribution</strong> (git blame on the lines
                    a bug-fix MR removed/modified — pins bugs to specific authors). Blame requires a one-time scan.
                </p>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', fontSize: '11px', color: PULSE_COLORS.textMuted, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Weeks
                        <input
                            type="number"
                            value={weeks}
                            onChange={(e) => onWeeksChange(Math.min(Math.max(Number(e.target.value) || 12, 1), 52))}
                            style={{ width: '50px', background: PULSE_COLORS.bgInner, border: `1px solid ${PULSE_COLORS.border}`, color: PULSE_COLORS.text, borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }}
                        />
                    </label>
                    {!authorsOverride.trim() && (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            Top N
                            <input
                                type="number"
                                value={topN}
                                onChange={(e) => onTopNChange(Math.min(Math.max(Number(e.target.value) || 5, 1), 20))}
                                style={{ width: '50px', background: PULSE_COLORS.bgInner, border: `1px solid ${PULSE_COLORS.border}`, color: PULSE_COLORS.text, borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }}
                            />
                        </label>
                    )}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        Specific authors
                        <input
                            type="text"
                            placeholder="Paul X, Helen Y, ..."
                            value={authorsOverride}
                            onChange={(e) => onAuthorsOverrideChange(e.target.value)}
                            style={{ width: '240px', background: PULSE_COLORS.bgInner, border: `1px solid ${PULSE_COLORS.border}`, color: PULSE_COLORS.text, borderRadius: '4px', padding: '4px 6px', fontSize: '12px' }}
                        />
                    </label>
                </div>
            </div>

            {note && <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>{note}</p>}

            {authors.length === 0 && !note && (
                <p style={{ color: PULSE_COLORS.textMuted, fontSize: '13px' }}>
                    No data — refresh the snapshot and re-run a scan, then return here.
                </p>
            )}

            {/* Blame coverage + scan controls */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', padding: '8px 12px', background: PULSE_COLORS.bgInner, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '6px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                <span>
                    <strong style={{ color: PULSE_COLORS.text }}>Blame coverage:</strong>{' '}
                    {coverage ? `${coverage.attributed}/${coverage.total} bug-fix MRs attributed (${coverage.scanned} scanned)` : '—'}
                </span>
                <button
                    type="button"
                    disabled={blameStatus?.running}
                    onClick={() => runBlameScan(30, 50)}
                    style={{ background: PULSE_COLORS.primary, color: PULSE_COLORS.bg, border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: blameStatus?.running ? 'wait' : 'pointer', fontSize: '11px', fontWeight: 600 }}
                >
                    {blameStatus?.running ? 'Scanning…' : '🔬 Scan blame (30d, 50 MRs)'}
                </button>
                <button
                    type="button"
                    disabled={blameStatus?.running}
                    onClick={() => runBlameScan(180, 200)}
                    style={{ background: 'transparent', color: PULSE_COLORS.text, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '4px', padding: '4px 10px', cursor: blameStatus?.running ? 'wait' : 'pointer', fontSize: '11px' }}
                >
                    {blameStatus?.running ? '…' : 'Deep blame scan (180d, 200 MRs)'}
                </button>
                {blameStatus?.summary && (
                    <span style={{ color: PULSE_COLORS.text }}>
                        Last scan: {blameStatus.summary.ok} ok · {blameStatus.summary.no_blame} no-blame · {blameStatus.summary.too_large} too-large · {blameStatus.summary.error} error
                    </span>
                )}
                {blameStatus?.error && <span style={{ color: '#f87171' }}>Error: {blameStatus.error}</span>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {authors.map((a) => (
                    <AuthorCard key={a.name} author={a} />
                ))}
            </div>
        </div>
    )
}

function AuthorCard({ author }) {
    const dwellStyle = { fontFamily: 'monospace' }
    const defectColor = author.bug_cohort?.defect_rate_per_100pts > 10 ? '#f87171'
        : author.bug_cohort?.defect_rate_per_100pts > 5 ? '#fbbf24'
        : '#4ade80'

    // Sparkline-style weekly chart — small inline SVG, no recharts to keep this view light.
    const weekly = author.weekly || []
    const maxPts = Math.max(1, ...weekly.map((w) => w.points))
    const barW = 16
    const gap = 3
    const height = 36

    return (
        <div
            style={{
                padding: '14px 16px',
                background: PULSE_COLORS.bgInner,
                border: `1px solid ${PULSE_COLORS.border}`,
                borderRadius: '10px',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
                <div style={{ color: PULSE_COLORS.text, fontWeight: 700, fontSize: '15px' }}>
                    {author.name}
                </div>
                <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px' }}>
                    {author.mr_count} MRs shipped · {author.epics_touched} epics
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '12px' }}>
                <Metric label="Shipped pts" value={author.shipped_points} sub={`${author.ai_share_pct}% AI`} />
                <Metric label="AI completed (snapshot)" value={author.ai_points_completed_snapshot} sub={`of ${author.total_points_completed_snapshot} total`} />
                <Metric
                    label="Eng dwell"
                    value={author.engineering_dwell_days.p50 != null ? `${author.engineering_dwell_days.p50}d` : '—'}
                    sub={author.engineering_dwell_days.p90 != null ? `p90 ${author.engineering_dwell_days.p90}d (${author.engineering_dwell_days.count_with_data})` : 'no data'}
                />
                <Metric
                    label="Pipeline dwell"
                    value={author.pipeline_dwell_days.p50 != null ? `${author.pipeline_dwell_days.p50}d` : '—'}
                    sub={author.pipeline_dwell_days.p90 != null ? `p90 ${author.pipeline_dwell_days.p90}d (${author.pipeline_dwell_days.count_with_data})` : 'no data'}
                />
                <Metric
                    label={`Bugs (same repo, ${author.bug_cohort?.window_days ?? 14}d)`}
                    value={author.bug_cohort?.bug_count ?? 0}
                    sub={`${author.bug_cohort?.defect_rate_per_100pts ?? 0}/100pts`}
                    color={defectColor}
                />
                <Metric
                    label={`Bugs (blame ≥${author.bug_cohort_blame?.share_threshold_pct ?? 25}%)`}
                    value={author.bug_cohort_blame?.bug_count ?? 0}
                    sub={`${author.bug_cohort_blame?.defect_rate_per_100pts ?? 0}/100pts`}
                    color={
                        author.bug_cohort_blame?.defect_rate_per_100pts > 10 ? '#f87171'
                            : author.bug_cohort_blame?.defect_rate_per_100pts > 5 ? '#fbbf24'
                            : '#4ade80'
                    }
                />
            </div>

            {weekly.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                    <div style={{ color: PULSE_COLORS.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '4px' }}>
                        Weekly shipped points (purple = AI subset)
                    </div>
                    <svg width={weekly.length * (barW + gap)} height={height + 14} style={{ display: 'block' }}>
                        {weekly.map((w, i) => {
                            const h = (w.points / maxPts) * height
                            const aiH = (w.ai_points / maxPts) * height
                            const x = i * (barW + gap)
                            return (
                                <g key={w.week_start}>
                                    <title>{`${w.week_start}: ${w.points} pts (${w.ai_points} AI) · ${w.mr_count} MRs`}</title>
                                    <rect x={x} y={height - h} width={barW} height={h} fill="#3b82f6" />
                                    <rect x={x} y={height - aiH} width={barW} height={aiH} fill="#a78bfa" />
                                </g>
                            )
                        })}
                    </svg>
                </div>
            )}

            {author.bug_cohort?.top_repos?.length > 0 && (
                <div style={{ marginTop: '10px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                    Top bug-loaded repos: {author.bug_cohort.top_repos.map((r) => (
                        <span key={r.repo} style={{ marginLeft: '6px', fontFamily: 'monospace' }}>
                            {r.repo.split('/').slice(-2).join('/')}
                            <span> ({r.count})</span>
                        </span>
                    ))}
                </div>
            )}
            {author.bug_cohort?.bug_keys?.length > 0 && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                    Same-repo bug keys: {author.bug_cohort.bug_keys.slice(0, 12).map((k) => (
                        <a key={k} href={`https://bstock.atlassian.net/browse/${k}`} target="_blank" rel="noreferrer" style={{ marginLeft: '6px', color: PULSE_COLORS.primary, textDecoration: 'none', fontFamily: 'monospace' }}>{k}</a>
                    ))}
                    {author.bug_cohort.bug_keys.length > 12 && <span> +{author.bug_cohort.bug_keys.length - 12} more</span>}
                </div>
            )}
            {author.bug_cohort_blame?.bug_keys?.length > 0 && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: PULSE_COLORS.textMuted }}>
                    Blame-attributed bug keys: {author.bug_cohort_blame.bug_keys.slice(0, 12).map((k) => (
                        <a key={k} href={`https://bstock.atlassian.net/browse/${k}`} target="_blank" rel="noreferrer" style={{ marginLeft: '6px', color: '#a78bfa', textDecoration: 'none', fontFamily: 'monospace' }}>{k}</a>
                    ))}
                    {author.bug_cohort_blame.bug_keys.length > 12 && <span> +{author.bug_cohort_blame.bug_keys.length - 12} more</span>}
                </div>
            )}
        </div>
    )
}

function Metric({ label, value, sub, color }) {
    return (
        <div style={{ padding: '8px 10px', background: PULSE_COLORS.cardBg, border: `1px solid ${PULSE_COLORS.border}`, borderRadius: '6px' }}>
            <div style={{ color: PULSE_COLORS.textMuted, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
            <div style={{ color: color || PULSE_COLORS.text, fontWeight: 700, fontSize: '18px', marginTop: '2px' }}>{value}</div>
            {sub && <div style={{ color: PULSE_COLORS.textMuted, fontSize: '10px', marginTop: '2px' }}>{sub}</div>}
        </div>
    )
}
